import React, { useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import toast from 'react-hot-toast';
import { useAdminActors, useCreateActor, useUpdateActor, useDeleteActor } from '../../hooks/useMovie';
import { useAlert } from '../../components/ui/AlertModal';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '../../components/ui/dialog';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
        {label}
        {hint && <span className="ml-1 font-normal text-muted-foreground/60">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function AdminActorsPage() {
  usePageTitle('Manage Actors · Kast');
  const { data: actors = [], isLoading } = useAdminActors();
  const createMutation = useCreateActor();
  const updateMutation = useUpdateActor();
  const deleteMutation = useDeleteActor();
  const { confirm } = useAlert();

  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null); // actor object or null
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  const resetForm = () => {
    setFormOpen(false); setEditing(null); setName(''); setBio(''); setImageFile(null); setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startCreate = () => { resetForm(); setFormOpen(true); };

  const startEdit = (actor) => {
    setEditing(actor); setName(actor.Name); setBio(actor.Bio || ''); setImageFile(null); setImagePreview(null); setFormOpen(true);
  };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Name is required.'); return; }
    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.Id, name: name.trim(), bio, imageFile });
        toast.success('Actor updated.');
      } else {
        await createMutation.mutateAsync({ name: name.trim(), bio, imageFile });
        toast.success('Actor added.');
      }
      resetForm();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save actor.'));
    }
  };

  const handleDelete = async (actor) => {
    const ok = await confirm(
      `Remove "${actor.Name}"? This removes them from every movie's cast list.`,
      { title: 'Delete Actor', variant: 'error', isConfirm: true, confirmLabel: 'Delete' }
    );
    if (!ok) return;
    try {
      await deleteMutation.mutateAsync(actor.Id);
      toast.success('Actor removed.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to delete actor.'));
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:users-three-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Actors</h1>
            <p className="text-xs text-muted-foreground">Manage the cast directory. Credit actors on individual titles from the Catalog page</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {actors.length > 0 && (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-primary/25">
              {actors.length} actor{actors.length === 1 ? '' : 's'}
            </span>
          )}
          <Button size="sm" onClick={startCreate} className="gap-1.5">
            <Icon icon="ph:plus-bold" />
            Add Actor
          </Button>
        </div>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Icon icon="ph:magnifying-glass-bold" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground/60" />
        <input
          className={`${inputClass} pl-9`}
          placeholder="Search actors…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : actors.length === 0 ? (
        <EmptyState icon="ph:user-bold">No actors yet — add one above</EmptyState>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {actors.map((actor) => (
            <div
              key={actor.Id}
              className="flex flex-col items-center gap-3 rounded-xl bg-white/[0.03] p-4 text-center ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.05]"
            >
              <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/5 text-muted-foreground/40">
                {actor.ImageUrl ? (
                  <img src={actor.ImageUrl} alt={actor.Name} className="h-full w-full object-cover" />
                ) : (
                  <Icon icon="ph:user-bold" className="size-8" />
                )}
              </div>
              <div className="min-w-0 w-full">
                <h3 className="truncate text-sm font-semibold text-foreground">{actor.Name}</h3>
                {actor.Bio && (
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{actor.Bio}</p>
                )}
              </div>
              <div className="mt-1 flex w-full items-center gap-2">
                <Button size="sm" variant="secondary" onClick={() => startEdit(actor)} className="flex-1 gap-1.5">
                  <Icon icon="ph:pencil-simple-bold" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleDelete(actor)}
                  disabled={deleteMutation.isPending}
                  aria-label={`Delete ${actor.Name}`}
                  className="shrink-0 gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
                >
                  <Icon icon="ph:trash-bold" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={formOpen} onOpenChange={(next) => { if (!next) resetForm(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon icon={editing ? 'ph:pencil-simple-bold' : 'ph:plus-bold'} />
              {editing ? 'Edit Actor' : 'Add Actor'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="contents">
            <DialogBody>
              <Field label="Name">
                <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
              </Field>
              <Field label="Bio">
                <textarea
                  className={`${inputClass} resize-none`}
                  rows={4}
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
              </Field>
              <Field label="Photo" hint={editing ? '(leave blank to keep current)' : undefined}>
                <div className="flex items-center gap-3">
                  {(imagePreview || (editing && editing.ImageUrl)) && (
                    <img
                      src={imagePreview || editing.ImageUrl}
                      alt="Preview"
                      className="size-14 shrink-0 rounded-full object-cover"
                    />
                  )}
                  <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-dashed border-white/15 bg-white/[0.02] px-3 py-2.5 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.04]">
                    <Icon icon="ph:image-bold" className="size-4 shrink-0" />
                    <span className="truncate">{imageFile ? imageFile.name : 'Choose image…'}</span>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImagePick} className="hidden" />
                  </label>
                </div>
              </Field>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={resetForm} className="gap-1.5">
                <Icon icon="ph:x-bold" />
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="gap-1.5">
                <Icon icon="ph:check-bold" />
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Actor'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
